import { Injector } from '@angular/core';
import moment, { Moment } from 'moment';
import { StoreConfig } from '../../config/store-config';

// Helpers for code that talks to several stores from one place (finance
// sync, return tracking, cancelled-order lookup), so the store-scoped
// injector trick and the Shopee window split are written once.

// Shopee caps its list endpoints (orders, escrow, returns) at a 15-day
// window per call; split conservatively at 14.
export const SHOPEE_LIST_WINDOW_DAYS = 14;

export interface DateWindow {
    from: Moment;
    to: Moment;
}

// Consecutive windows of at most `days` covering [from, to], each starting
// one second after the previous one ends so no row is fetched twice.
export const splitWindows = (
    from: Moment,
    to: Moment,
    days: number,
): DateWindow[] => {
    const windows: DateWindow[] = [];
    let cursor = from.clone();
    while (cursor.isBefore(to)) {
        const end = moment.min(cursor.clone().add(days, 'days'), to.clone());
        windows.push({ from: cursor.clone(), to: end });
        cursor = end.clone().add(1, 'second');
    }
    return windows;
};

// A platform service instance bound to one store. The platform services
// read their credentials from a DI token rather than an argument, so each
// store needs its own injector — same shape as buildStoreContext in
// combined-orders.component.ts.
export const storeService = <T>(
    parent: Injector,
    store: StoreConfig,
    authInfoToken: string,
    serviceClass: new (...args: any[]) => T,
): T => {
    const injector = Injector.create({
        providers: [
            { provide: authInfoToken, useValue: store.authInfo },
            { provide: serviceClass, useClass: serviceClass, deps: [] },
        ],
        parent,
    });
    return injector.get(serviceClass);
};
